<template>
	<div class="ai-chat-settings-page">
		<!-- Vuetify Tabs -->
		<v-tabs class="no-uppercase-tabs" v-model="activeTab" align-tabs="start" color="primary">
			<v-tab value="preloads">
				<span class="tab-icon">📋</span>
				Pre Load Instructions
			</v-tab>
			<v-tab value="contexts">
				<span class="tab-icon">🔗</span>
				Context Hints
			</v-tab>
			<v-tab value="test-chat">
				<span class="tab-icon">💬</span>
				Test Chat
			</v-tab>
		</v-tabs>

		<v-window v-model="activeTab">
			<!-- Tab 1: Pre Load Instructions -->
			<v-window-item value="preloads">
				<div class="tab-content">
					<admin-table
						ref="preloadsTable"
						title="Pre Load Instructions"
						icon="📋"
						:columns="preloadColumns"
						:data="preloads"
						:loading="preloadsLoading"
						:saving-row-id="savingPreloadId"
						:adding-row="addingPreload"
						:deleting-row-id="deletingPreloadId"
						:searchable="true"
						search-placeholder="Search instructions..."
						:editable="true"
						:deletable="true"
						:addable="true"
						empty-message="No pre-load instructions configured."
						@row-edit-save="handlePreloadEditSave"
						@row-delete="handlePreloadDelete"
						@row-add="handlePreloadAdd"
					>
						<template #cell-instruction="{ row }">
							<div class="instruction-cell-wrapper">
								<span :class="{'truncated': !isInstructionExpanded(row.id) && isTruncatable(row.instruction)}">
									{{ row.instruction }}
								</span>
								<button
									v-if="isTruncatable(row.instruction)"
									class="more-less-btn"
									@click.stop="toggleInstruction(row.id)"
								>
									{{ isInstructionExpanded(row.id) ? '[less]' : '[more]' }}
								</button>
							</div>
						</template>
					</admin-table>
				</div>
			</v-window-item>

			<!-- Tab 2: Context Hints -->
			<v-window-item value="contexts">
				<div class="tab-content">
					<admin-table
						ref="contextsTable"
						title="Context Hints"
						icon="🔗"
						:columns="contextColumns"
						:data="contexts"
						:loading="contextsLoading"
						:saving-row-id="savingContextId"
						:adding-row="addingContext"
						:deleting-row-id="deletingContextId"
						:searchable="true"
						search-placeholder="Search context hints..."
						:editable="true"
						:deletable="true"
						:addable="true"
						empty-message="No context hints configured."
						@row-edit-save="handleContextEditSave"
						@row-delete="handleContextDelete"
						@row-add="handleContextAdd"
					/>
				</div>
			</v-window-item>

			<!-- Tab 3: Test Chat -->
			<v-window-item value="test-chat">
				<div class="tab-content test-chat-section">
					<div class="chat-container">
						<h2>Test Chat MG</h2>

						<div class="form-group">
							<label>Provider:</label>
							<v-text-field
								v-model="message.providerOverride"
								variant="outlined"
								density="compact"
								placeholder="OpenAI"
							/>
						</div>

						<div class="chat-display">
							<v-textarea
								v-model="chatResponse"
								variant="outlined"
								readonly
								rows="15"
								class="chat-output"
								placeholder="Response will appear here..."
							/>
						</div>

						<div class="chat-input-group">
							<v-textarea
								v-model="message.body"
								variant="outlined"
								rows="3"
								placeholder="Enter your message..."
								class="chat-input"
							/>
							<div class="button-row">
								<MGButton
									text="Send"
									@click="sendMessage"
									:disabled="!message.body || loading"
									:small="true"
									class="send-button"
								/>
							</div>
						</div>
					</div>
				</div>
			</v-window-item>
		</v-window>

		<!-- Delete Pre-Load Instruction Confirmation Dialog -->
		<MGDialog
			v-model="showDeletePreloadDialog"
			title="Delete Pre-Load Instruction"
			max-width="400px"
			:persistent="true"
		>
			<template #content>
				<p>Are you sure you want to delete this pre-load instruction?</p>
				<p v-if="itemToDelete" class="instruction-preview">
					<strong>From:</strong> {{ itemToDelete.from }}
				</p>
				<p v-if="itemToDelete" class="instruction-preview"><strong>Instruction:</strong> {{ itemToDelete.instruction }}</p>
			</template>
			<template #actions>
				<MGButton
					text="Delete"
					@click="confirmDeletePreload"
					:stacked="true"
					outline
				/>
				<MGButton
					text="Cancel"
					@click="cancelDeletePreload"
					:stacked="true"
				/>
			</template>
		</MGDialog>

		<!-- Delete Context Hint Confirmation Dialog -->
		<MGDialog
			v-model="showDeleteContextDialog"
			title="Delete Context Hint"
			max-width="400px"
			:persistent="true"
		>
			<template #content>
				<p>Are you sure you want to delete this context hint?</p>
				<p v-if="itemToDelete" class="instruction-preview"><strong>Keywords:</strong> {{ itemToDelete.keyWords }}</p>
			</template>
			<template #actions>
				<MGButton
					text="Delete"
					@click="confirmDeleteContext"
					:stacked="true"
					outline
				/>
				<MGButton
					text="Cancel"
					@click="cancelDeleteContext"
					:stacked="true"
				/>
			</template>
		</MGDialog>
	</div>
</template>

<script>
import AdminTable from '../components/AdminTable.vue';
import MGDialog from '@/components/elements/MGDialog.vue';
import MGButton from '@/components/elements/MGButton.vue';
import AdminServices from '../mixins/AdminServices.js';

export default {
	name: 'AIChatSettingsPage',
	components: {
		AdminTable,
		MGDialog,
		MGButton
	},
	mixins: [AdminServices],
	data() {
		return {
			activeTab: 'preloads',
			expandedInstructions: [], // For 'more/less' link on mobile

			// Dialog state
			showDeletePreloadDialog: false,
			showDeleteContextDialog: false,
			itemToDelete: null,
			deleteType: null, // 'preload' or 'context'
			
			// Pre Load Instructions
			preloads: [],
			preloadsLoading: false,
			savingPreloadId: null,
			addingPreload: false,
			deletingPreloadId: null,
			preloadColumns: [
				{
					field: 'from',
					label: 'From',
					width: '150px',
					sortable: true,
					editable: true,
					type: 'text',
					required: true,
					placeholder: 'System'
				},
				{
					field: 'instruction',
					label: 'Instruction',
					width: '3fr',
					sortable: false,
					editable: true,
					type: 'textarea',
					required: true,
					placeholder: 'Enter instruction...'
				}
			],
			// Context Hints
			contexts: [],
			contextsLoading: false,
			savingContextId: null,
			addingContext: false,
			deletingContextId: null,
			contextColumns: [
				{
					field: 'keyWords',
					label: 'Keywords',
					width: '200px',
					sortable: true,
					editable: true,
					type: 'text',
					required: true,
					placeholder: 'Keywords'
				},
				{
					field: 'cta',
					label: 'CTA',
					width: '2fr',
					sortable: false,
					editable: true,
					type: 'text',
					required: true,
					placeholder: 'Call to action'
				},
				{
					field: 'appScreen',
					label: 'App Screen',
					width: '200px',
					sortable: true,
					editable: true,
					type: 'text',
					required: false,
					placeholder: 'Screen name'
				},
				{
					field: 'cmsPage',
					label: 'CMS Page',
					width: '200px',
					sortable: true,
					editable: true,
					type: 'text',
					required: false,
					placeholder: 'CMS page'
				},
				{
					field: 'externalUrl',
					label: 'External URL',
					width: '200px',
					sortable: false,
					editable: true,
					type: 'url',
					required: false,
					placeholder: 'https://...'
				}
			],
			// Test Chat
			message: { body: "", providerOverride: "OpenAI" },
			self: null,
			chatResponse: "",
			loading: false
		}
	},
	methods: {
		// Text expansion for mobile
		isInstructionExpanded(rowId) {
			return this.expandedInstructions.includes(rowId);
		},
		toggleInstruction(rowId) {
			const index = this.expandedInstructions.indexOf(rowId);
			if (index > -1) {
				this.expandedInstructions.splice(index, 1);
			} else {
				this.expandedInstructions.push(rowId);
			}
		},
		isTruncatable(text) {
			if (!text) return false;
			const lineCount = (text.match(/\n/g) || []).length + 1;
			return lineCount > 3 || text.length > 150;
		},

		// Load all data
		onLoad() {
			this.getSelf((result) => {
				this.self = result;
			});
			this.getPreLoadInstructions();
			this.getContextAppActions();
		},

		// Pre Load Instructions
		getPreLoadInstructions() {
			this.preloadsLoading = true;
			this._getPreLoadInstructions((result) => {
				this.preloads = result;
				this.preloadsLoading = false;
			});
		},

		handlePreloadAdd(data) {
			console.log('handlePreloadAdd called, setting addingPreload to true');
			this.addingPreload = true;
			console.log('addingPreload is now:', this.addingPreload);
			this._createPreLoadInstruction(data,
				() => {
					this.getPreLoadInstructions();
					this.$refs.preloadsTable?.resetAddRow();
					this.addingPreload = false;
				},
				(error) => {
					console.error('Error adding pre-load instruction:', error);
					this.addingPreload = false;
				}
			);
		},

		handlePreloadEditSave({ rowId, data }) {
			this.savingPreloadId = rowId;
			this._updatePreLoadInstruction(rowId, data,
				() => {
					this.getPreLoadInstructions();
					this.$refs.preloadsTable?.cancelEdit();
					this.savingPreloadId = null;
				},
				(error) => {
					console.error('Error saving pre-load instruction:', error);
					this.savingPreloadId = null;
				}
			);
		},

		handlePreloadDelete(row) {
			this.itemToDelete = row;
			this.deleteType = 'preload';
			this.showDeletePreloadDialog = true;
		},

		confirmDeletePreload() {
			if (!this.itemToDelete) return;

			const idToDelete = this.itemToDelete.id;
			this.showDeletePreloadDialog = false;
			this.deletingPreloadId = idToDelete;

			this._deletePreLoadInstruction(idToDelete,
				() => {
					this.getPreLoadInstructions();
					this.itemToDelete = null;
					this.deletingPreloadId = null;
				},
				(error) => {
					console.error('Error deleting pre-load instruction:', error);
					this.deletingPreloadId = null;
				}
			);
		},

		cancelDeletePreload() {
			this.showDeletePreloadDialog = false;
			this.itemToDelete = null;
		},

		// Context Hints
		getContextAppActions() {
			this.contextsLoading = true;
			this._getContextAppActions((result) => {
				this.contexts = result;
				this.contextsLoading = false;
			});
		},

		handleContextAdd(data) {
			this.addingContext = true;
			this._createContextAppAction(data,
				() => {
					this.getContextAppActions();
					this.$refs.contextsTable?.resetAddRow();
					this.addingContext = false;
				},
				(error) => {
					console.error('Error adding context hint:', error);
					this.addingContext = false;
				}
			);
		},

		handleContextEditSave({ rowId, data }) {
			this.savingContextId = rowId;
			this._updateContextAppAction(rowId, data,
				() => {
					this.getContextAppActions();
					this.$refs.contextsTable?.cancelEdit();
					this.savingContextId = null;
				},
				(error) => {
					console.error('Error saving context hint:', error);
					this.savingContextId = null;
				}
			);
		},

		handleContextDelete(row) {
			this.itemToDelete = row;
			this.deleteType = 'context';
			this.showDeleteContextDialog = true;
		},

		confirmDeleteContext() {
			if (!this.itemToDelete) return;

			const idToDelete = this.itemToDelete.id;
			this.showDeleteContextDialog = false;
			this.deletingContextId = idToDelete;

			this._deleteContextAppAction(idToDelete,
				() => {
					this.getContextAppActions();
					this.itemToDelete = null;
					this.deletingContextId = null;
				},
				(error) => {
					console.error('Error deleting context hint:', error);
					this.deletingContextId = null;
				}
			);
		},

		cancelDeleteContext() {
			this.showDeleteContextDialog = false;
			this.itemToDelete = null;
		},

		// Test Chat
		sendMessage() {
			if (!this.message.body || !this.self) return;

			this.loading = true;
			this.chatResponse = "Please wait . . .";

			this._sendChatMessage(this.self.id, this.message,
				(result) => {
					this.chatResponse = result.body;
					this.loading = false;
				},
				(error) => {
					this.chatResponse = `Error: ${error}`;
					this.loading = false;
				}
			);
		}
	},
	mounted() {
		// Initialize api property from route meta for mixin methods
		this.api = this.$route.meta?.api || 'https://api.sysodev.link';
		this.onLoad();
	}
}
</script>

<style scoped>
.ai-chat-settings-page {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
}

/* Tabs */
.tab-icon {
	margin-right: 8px;
}

/* Override Vuetify tab uppercase styling for this component only */
/* Broader/deeper override to cover different Vuetify versions and internal markup */
/* Target common internal elements: v-tab, v-tab__wrapper, v-tab__content, v-btn, etc. */

/* Use a class on the v-tabs element and target its internals to avoid global overrides */
:deep(.no-uppercase-tabs .v-tab),
:deep(.no-uppercase-tabs .v-tab *),
:deep(.no-uppercase-tabs .v-tab .v-tab__wrapper),
:deep(.no-uppercase-tabs .v-tab .v-tab__content),
:deep(.no-uppercase-tabs .v-tab .v-btn),
:deep(.no-uppercase-tabs .v-tab .v-btn__content) {
	text-transform: none !important;
}

/* As a last-resort catch-all for this component only (keeps global styles untouched) */
:deep(.ai-chat-settings-page .v-tabs) * {
	text-transform: none !important;
}

/* Tab Content */
.tab-content {
	padding: 24px;
	min-height: 400px;
}

/* Test Chat Section */
.test-chat-section {
	max-width: 900px;
	margin: 0 auto;
}

.chat-container h2 {
	margin-bottom: 24px;
	color: var(--admin-text-primary, #1a1a1a);
}

.form-group {
	margin-bottom: 20px;
}

.form-group label {
	display: block;
	margin-bottom: 8px;
	font-size: 13px;
	font-weight: 600;
	color: var(--admin-text-secondary, #666);
}

.chat-display {
	margin-bottom: 20px;
}

.chat-output :deep(textarea) {
	background-color: #1e1e1e !important;
	color: #d4d4d4 !important;
	font-family: 'Courier New', 'Monaco', monospace !important;
	font-size: 13px;
	line-height: 1.6;
}

.chat-output :deep(.v-field__field) {
	background-color: #1e1e1e;
}

.chat-input-group {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.chat-input {
	width: 100%;
}

.button-row {
	display: flex;
	justify-content: left;
}

.send-button {
	white-space: nowrap;
}

/* Dialog Styles */
.instruction-preview {
	margin-top: 16px;
	padding: 12px;
	background-color: var(--admin-hover-bg, #f5f5f5);
	border-radius: 4px;
	font-size: 13px;
	color: var(--admin-text-primary, #1a1a1a);
}

/* Responsive */
@media (max-width: 768px) {
	.tab-content {
		padding: 16px;
	}

	.test-chat-section {
		max-width: 100%;
	}

	.chat-input-group {
		flex-direction: column;
		align-items: stretch;
	}

	.chat-input-group :deep(.v-btn) {
		width: 100%;
	}
}

.instruction-cell-wrapper {
	white-space: normal;
	word-break: break-word;
}

.more-less-btn {
	background: none;
	border: none;
	color: var(--admin-text-primary); /* Use admin primary text color for theme consistency */
	cursor: pointer;
	font-size: 13px;
	padding: 4px 0 0 0;
	margin: 0;
	display: none; /* Hidden on desktop */
	font-weight: bold;
}

/* Apply truncation and show button only on mobile */
@media (max-width: 480px) {
	.instruction-cell-wrapper .truncated {
		display: -webkit-box;
		-webkit-line-clamp: 3; /* Limit to 3 lines as requested */
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.more-less-btn {
		display: block; /* Show on mobile */
	}
}
</style>
